import { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { LogOut, RefreshCw, Users, Eye, Clock, CalendarDays, MousePointerClick } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#BF9B3A";
const GOLD_SOFT = "#E4D29A";
const INK = "#2a2520";
const PIE = [GOLD, "#8C7327", "#D9C27E", "#B0B0A8"];

const RANGES = [
  { label: "7 Days", days: 7 },
  { label: "30 Days", days: 30 },
  { label: "All Time", days: 0 },
];

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[var(--gold-line)] p-6 lg:p-7 flex flex-col gap-3"
    >
      <span className="flex items-center gap-2 font-marcellus uppercase tracking-[0.28em] text-[0.6rem] text-[var(--gold-deep)]">
        <Icon size={14} strokeWidth={1.5} /> {label}
      </span>
      <span className="font-couture text-4xl lg:text-5xl text-[var(--ink)] leading-none tabular-nums">
        {value}
      </span>
      {sub && (
        <span className="font-couture-italic text-sm text-[var(--ink-soft)]">{sub}</span>
      )}
    </div>
  );
}

function Panel({ title, children, testid, className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`bg-white border border-[var(--gold-line)] p-6 lg:p-8 ${className}`}
    >
      <p className="font-marcellus uppercase tracking-[0.34em] text-[0.62rem] text-[var(--gold-deep)] mb-7">
        {title}
      </p>
      {children}
    </div>
  );
}

const tip = {
  contentStyle: {
    background: "#fff",
    border: "1px solid #E7DCC0",
    borderRadius: 0,
    fontFamily: "Marcellus, serif",
    fontSize: 12,
  },
};

export default function AnalyticsDashboard({ token, onLogout, onAuthError }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/analytics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        setLoading(false);
        return onAuthError();
      }
      if (!res.ok) throw new Error("Failed to load analytics");
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, token, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const maxPage = data ? Math.max(1, ...data.top_pages.map((p) => p.count)) : 1;
  const totalDevices = data ? data.devices.reduce((a, d) => a + d.count, 0) : 0;

  return (
    <div data-testid="analytics-dashboard" className="min-h-screen bg-[var(--bg-soft)]">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-[var(--gold-line)]">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-6 lg:px-10 py-4 lg:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <p className="font-marcellus uppercase tracking-[0.4em] text-[0.58rem] text-[var(--gold-deep)]">
              Dr. P. B. Boss
            </p>
            <h1 className="font-couture text-2xl lg:text-3xl text-[var(--ink)] tracking-[-0.01em]">
              Analytics Console
            </h1>
          </div>
          <div className="flex items-center flex-wrap gap-2 lg:gap-3">
            <div className="flex border border-[var(--gold-line)] rounded-none overflow-hidden">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  data-testid={`range-${r.days}`}
                  onClick={() => setDays(r.days)}
                  className={`px-3 lg:px-4 py-2 font-marcellus uppercase tracking-[0.2em] text-[0.58rem] transition-colors ${
                    days === r.days
                      ? "bg-[var(--gold-deep)] text-white"
                      : "bg-white text-[var(--ink-soft)] hover:bg-[var(--bg-soft)]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="w-10 h-10 flex items-center justify-center border border-[var(--gold-line)] text-[var(--gold-deep)] hover:bg-white transition-colors"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 px-4 py-2.5 border border-[var(--gold-line)] text-[var(--ink)] font-marcellus uppercase tracking-[0.2em] text-[0.58rem] hover:bg-white transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8 lg:py-12 space-y-8">
        {error && (
          <p className="text-red-700 font-marcellus">{error}</p>
        )}
        {!data && loading && (
          <p className="font-couture-italic text-[var(--ink-soft)]">Loading insights…</p>
        )}

        {data && (
          <>
            {/* KPI cards */}
            <section className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
              <StatCard testid="kpi-visitors" icon={Users} label="Unique Visitors" value={t.unique_visitors.toLocaleString()} />
              <StatCard testid="kpi-pageviews" icon={Eye} label="Page Views" value={t.pageviews.toLocaleString()} />
              <StatCard testid="kpi-sessions" icon={MousePointerClick} label="Sessions" value={t.sessions.toLocaleString()} />
              <StatCard
                testid="kpi-peak-hour"
                icon={Clock}
                label="Peak Hour"
                value={data.peak_hour && t.pageviews ? data.peak_hour.label : "—"}
                sub={data.peak_hour && t.pageviews ? `${data.peak_hour.count} views (IST)` : "No data yet"}
              />
              <StatCard
                testid="kpi-peak-day"
                icon={CalendarDays}
                label="Busiest Day"
                value={data.peak_day && t.pageviews ? data.peak_day.short : "—"}
                sub={data.peak_day && t.pageviews ? `${data.peak_day.count} views` : "No data yet"}
              />
            </section>

            {/* Traffic over time */}
            <Panel testid="panel-traffic" title="Traffic Over Time">
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.by_date} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFE7D2" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8a8275" }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8a8275" }} />
                  <Tooltip {...tip} />
                  <Area type="monotone" dataKey="count" name="Views" stroke={GOLD} strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            {/* Busiest hours + days */}
            <section className="grid lg:grid-cols-2 gap-6 lg:gap-8">
              <Panel testid="panel-hours" title="Busiest Hours (IST)">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.by_hour} margin={{ top: 5, right: 6, left: -22, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFE7D2" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#8a8275" }} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8a8275" }} />
                    <Tooltip {...tip} labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`} />
                    <Bar dataKey="count" name="Views" fill={GOLD} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel testid="panel-weekdays" title="Busiest Days">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.by_weekday} margin={{ top: 5, right: 6, left: -22, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EFE7D2" vertical={false} />
                    <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#8a8275" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8a8275" }} />
                    <Tooltip {...tip} />
                    <Bar dataKey="count" name="Views" fill={GOLD_SOFT} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            </section>

            {/* Top pages, referrers, devices */}
            <section className="grid lg:grid-cols-3 gap-6 lg:gap-8">
              <Panel testid="panel-top-pages" title="Top Pages">
                {data.top_pages.length === 0 && <p className="font-couture-italic text-[var(--ink-soft)]">No data yet.</p>}
                <ul className="space-y-4">
                  {data.top_pages.map((p, i) => (
                    <li key={i} data-testid={`top-page-${i}`}>
                      <div className="flex justify-between items-baseline mb-1.5">
                        <span className="font-marcellus text-sm text-[var(--ink)] truncate pr-3">{p.path}</span>
                        <span className="numeral text-sm text-[var(--gold-deep)] tabular-nums">{p.count}</span>
                      </div>
                      <div className="h-1.5 bg-[var(--bg-soft)]">
                        <div className="h-full bg-[var(--gold-deep)]" style={{ width: `${(p.count / maxPage) * 100}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel testid="panel-referrers" title="Top Referrers">
                {data.referrers.length === 0 && <p className="font-couture-italic text-[var(--ink-soft)]">No data yet.</p>}
                <ul className="divide-y divide-[var(--gold-line-soft)]">
                  {data.referrers.map((r, i) => (
                    <li key={i} data-testid={`referrer-${i}`} className="flex justify-between items-baseline py-3">
                      <span className="font-couture-italic text-base text-[var(--ink)] truncate pr-3">{r.source}</span>
                      <span className="numeral text-sm text-[var(--gold-deep)] tabular-nums">{r.count}</span>
                    </li>
                  ))}
                </ul>
              </Panel>

              <Panel testid="panel-devices" title="Devices">
                {totalDevices === 0 ? (
                  <p className="font-couture-italic text-[var(--ink-soft)]">No data yet.</p>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={data.devices} dataKey="count" nameKey="device" innerRadius={45} outerRadius={75} paddingAngle={2}>
                          {data.devices.map((d, i) => (
                            <Cell key={i} fill={PIE[i % PIE.length]} />
                          ))}
                        </Pie>
                        <Tooltip {...tip} />
                      </PieChart>
                    </ResponsiveContainer>
                    <ul className="mt-5 space-y-2.5">
                      {data.devices.map((d, i) => (
                        <li key={i} data-testid={`device-${i}`} className="flex items-center justify-between">
                          <span className="flex items-center gap-2.5 font-marcellus text-sm text-[var(--ink)]">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} />
                            {d.device}
                          </span>
                          <span className="numeral text-sm text-[var(--gold-deep)] tabular-nums">
                            {Math.round((d.count / totalDevices) * 100)}%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Panel>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
